import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useUpdateProfileBusinessPartnerMutation } from '../../../app/services/chatbot-rtk.services';
import { IPayoutInfo } from './partnerPayout';

export interface IProfileFormData {
  address1: string;
  address2: string;
  birthDate: string;
  city: string;
  companyName: string;
  country: string;
  firstName: string;
  lastName: string;
  phone: string;
  monthlyViews: string;
  textReaded: boolean;
  promotionPlan: string;
  ambitions: string;
  termsAndConditions: boolean;
  websiteLinks: string[];
  zipCode: string;
  isValidated: boolean;
  payoutInfo?: IPayoutInfo;
}

const PartnerProfile = ({ profile }: { profile: IProfileFormData }) => {
  const { t } = useTranslation();

  const [updateProfileBusinessPartner, updateProfileBusinessPartnerStatus] = useUpdateProfileBusinessPartnerMutation();

  const [profileInformation, setProfileInformation] = useState<IProfileFormData | null>(null);

  useEffect(() => {
    setProfileInformation(profile);
    const resultArray = Object.entries(profile);
    resultArray.forEach((item: any) => {
      setValue(item[0], item[1]);
    });
  }, [profile]);

  // Define the validation schema using Yup
  const schema = Yup.object({
    firstName: Yup.string()
      .required(t('BusinessInformation/Step1/fields/firstName/errors/required'))
      .max(30, t('BusinessInformation/Step1/fields/firstName/errors/maxLength')),
    lastName: Yup.string().required(t('BusinessInformation/Step1/fields/lastName/errors/required')),
    companyName: Yup.string().required(t('BusinessInformation/Step1/fields/companyName/errors/required')),
    address1: Yup.string(),
    address2: Yup.string(),
    city: Yup.string(),
    country: Yup.string().required(t('BusinessInformation/Step1/fields/country/errors/required')),
    state: Yup.string(),
    zipcode: Yup.string(),
    phone: Yup.string(),
    birthDate: Yup.string().required(t('BusinessInformation/Step1/fields/birthDate/errors/required')),
  });

  // Use React Hook Form
  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    watch,
  } = useForm<IProfileFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: profileInformation as any,
  });

  // Form submit handler
  const onSubmit = (data: any) => {
    updateProfileBusinessPartner(data)
      .unwrap()
      .then(res => {
        toast.success('Successfully edited.');
      })
      .catch(err => {
        toast.error(err?.data?.message || err?.message);
      });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col lg:tw-p-5 tw-p-2">
      <div className=" tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-12 tw-flex">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
            <div className="tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">Profile</div>

            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
              <div className="tw-self-stretch tw-h-px tw-border tw-border-gray-100"></div>
              <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex lg:tw-flex-row tw-flex-col  ">
                <div className="tw-w-full lg:tw-w-auto tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                  <Form.Group className="tw-w-full">
                    <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('BusinessInformation/Step1/fields/firstName/label')}
                    </Form.Label>
                    <Form.Control
                      isInvalid={!!errors.firstName}
                      maxLength={60}
                      {...register('firstName')}
                      className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                    />
                    <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                      {errors.firstName?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="tw-w-full lg:tw-w-auto tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                  <Form.Group className="tw-w-full">
                    <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('BusinessInformation/Step1/fields/lastName/label')}
                    </Form.Label>
                    <Form.Control
                      isInvalid={!!errors.lastName}
                      {...register('lastName')}
                      className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                    />
                    <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                      {errors.lastName?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="tw-w-full lg:tw-w-auto tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                  <Form.Group className="tw-w-full">
                    <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('BusinessInformation/Step1/fields/birthDate/label')}
                    </Form.Label>
                    <Form.Control
                      isInvalid={!!errors.birthDate}
                      type="date"
                      {...register('birthDate')}
                      className="tw-w-full lg:tw-w-80  self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                    />
                    <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                      {errors.birthDate?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
              <div className="tw-justify-start tw-items-start tw-gap-6 tw-inline-flex tw-w-full">
                <div className="tw-justify-start tw-items-start tw-gap-5 tw-flex tw-w-full lg:tw-w-auto">
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {t('BusinessInformation/Step1/fields/companyName/label')}
                      </Form.Label>
                      <Form.Control
                        isInvalid={!!errors.companyName}
                        {...register('companyName')}
                        className="tw-w-full lg:tw-w-80  self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.companyName?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>
                <div className="tw-justify-start tw-items-start tw-gap-5 tw-flex tw-w-full lg:tw-w-auto">
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">Website links</Form.Label>
                      <Form.Control
                        isInvalid={!!errors.websiteLinks}
                        {...register('websiteLinks')}
                        className="tw-w-full lg:tw-w-80  self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.websiteLinks?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>
              </div>
              <div className="tw-self-stretch tw-h-px tw-border tw-border-gray-100 tw-border-solid"></div>
              <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex tw-w-full">
                <div className="tw-justify-start tw-items-start tw-gap-5 tw-inline-flex tw-w-full">
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {t('BusinessInformation/Step1/fields/address1/label')}
                      </Form.Label>
                      <Form.Control
                        isInvalid={!!errors.address1}
                        {...register('address1')}
                        className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.address1?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>
                <div className="tw-justify-start tw-items-start tw-gap-5 tw-inline-flex tw-w-full">
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {t('BusinessInformation/Step1/fields/address2/label')}
                      </Form.Label>
                      <Form.Control
                        {...register('address2')}
                        className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className="tw-justify-start tw-items-start tw-gap-6 tw-flex lg:tw-flex-row tw-flex-col lg:tw-w-auto tw-w-full">
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {t('BusinessInformation/Step1/fields/country/label')}
                      </Form.Label>
                      <Form.Control
                        isInvalid={!!errors.country}
                        {...register('country')}
                        className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.country?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {t('BusinessInformation/Step1/fields/city/label')}
                      </Form.Label>
                      <Form.Control
                        isInvalid={!!errors.city}
                        {...register('city')}
                        className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.city?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>
                <div className="tw-justify-start tw-items-start tw-gap-6 tw-inline-flex tw-w-full">
                  <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-w-full">
                    <Form.Group className="tw-w-full">
                      <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {t('BusinessInformation/Step1/fields/zipcode/label')}
                      </Form.Label>
                      <Form.Control
                        isInvalid={!!errors.zipCode}
                        {...register('zipCode')}
                        className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                        {errors.zipCode?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>
                <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex tw-w-full">
                  <Form.Group className="tw-w-full">
                    <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('BusinessInformation/Step1/fields/phone/label')}
                    </Form.Label>
                    <Form.Control
                      isInvalid={!!errors.phone}
                      {...register('phone')}
                      className="lg:tw-w-80 tw-w-full self-stretch px-3 py-2 bg-white rounded-md border border-gray-300 justify-start items-center inline-flex"
                    />
                    <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                      {errors.phone?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-self-stretch tw-h-14 tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
            <div className="tw-self-stretch tw-h-[1px] tw-border tw-border-gray-100 tw-border-solid" />
            <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
              <div className="tw-px-4 tw-py-2 tw-opacity-0 tw-bg-primary tw-rounded-md tw-shadow tw-justify-center tw-items-center tw-flex">
                <div className="tw-text-white tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight">Apply changes</div>
              </div>
              <PSButton
                id="btn-partner-profile-submit"
                type="submit"
                className="tw-px-5 tw-py-1.5 tw-bg-primary tw-rounded-full tw-shadow tw-justify-center tw-items-center tw-flex"
              >
                <div className="tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">Edit</div>
              </PSButton>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default WrappedErrorBoundary(PartnerProfile);
